import { useState, FormEvent } from 'react';

const subjects = [
  'General Question',
  'Feature Request',
  'Billing & Plans',
  'Bug Report',
  'Partnership',
  'Press Inquiry',
  'Other',
];

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('sending');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d72vaa2fda70vjfplvag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-8 lg:p-10">
      <h2 className="text-xl font-black text-stone-900 mb-1">Send us a message</h2>
      <p className="text-sm text-stone-400 mb-8">We&apos;ll get back to you at support@adforgeai.app</p>

      {status === 'success' ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-orange-50 border border-orange-100 mb-5">
            <i className="ri-check-line text-3xl text-orange-500" />
          </div>
          <h3 className="text-lg font-bold text-stone-900 mb-2">Message sent!</h3>
          <p className="text-sm text-stone-500 max-w-xs">
            Thanks for reaching out. We&apos;ll reply to your email within a few hours.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 text-sm font-semibold text-orange-500 hover:text-orange-600 underline underline-offset-4 cursor-pointer transition-colors duration-200"
          >
            Send another message
          </button>
        </div>
      ) : (
        <form
          id="contact-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
                Full Name <span className="text-orange-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="Jane Smith"
                className="w-full text-sm border border-stone-200 rounded-lg px-4 py-3 text-stone-800 placeholder-stone-300 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-all duration-200"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
                Email Address <span className="text-orange-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="jane@business.com"
                className="w-full text-sm border border-stone-200 rounded-lg px-4 py-3 text-stone-800 placeholder-stone-300 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-all duration-200"
              />
            </div>
          </div>

          <div>
            <label htmlFor="subject" className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
              Subject <span className="text-orange-500">*</span>
            </label>
            <select
              id="subject"
              name="subject"
              required
              defaultValue=""
              className="w-full text-sm border border-stone-200 rounded-lg px-4 py-3 text-stone-800 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-all duration-200 cursor-pointer bg-white appearance-none"
            >
              <option value="" disabled>Select a topic…</option>
              {subjects.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="message" className="block text-xs font-semibold text-stone-600 uppercase tracking-wide mb-1.5">
              Message <span className="text-orange-500">*</span>
            </label>
            <div className="relative">
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                maxLength={500}
                placeholder="Tell us how we can help…"
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full text-sm border border-stone-200 rounded-lg px-4 py-3 text-stone-800 placeholder-stone-300 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition-all duration-200 resize-none"
              />
              <span className={`absolute bottom-3 right-3 text-xs font-medium ${charCount >= 500 ? 'text-red-400' : 'text-stone-300'}`}>
                {charCount}/500
              </span>
            </div>
            {charCount >= 500 && (
              <p className="text-xs text-red-400 mt-1">Message cannot exceed 500 characters.</p>
            )}
          </div>

          {status === 'error' && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-500 text-sm rounded-lg px-4 py-3">
              <i className="ri-error-warning-line" />
              Something went wrong. Please try again or email us directly.
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'sending' || charCount > 500}
            className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {status === 'sending' ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill" />
                Send Message
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
